import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  AddCircleOutlineOutlined,
  Autorenew,
  DeleteOutline,
  EditOutlined,
} from "@mui/icons-material";
import {
  Button,
  DialogContent,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import {
  BillingContact,
  ContactType,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_contact_pb";

import {
  getBillingContactsByOrg,
  updateBillingContacts,
} from "api/endpoints/subscription";
import { gRPCStatus } from "api/grpc-status-codes";
import { callOptionsHeaders } from "api/helpers";
import PillStatusTrigger from "components/org-display/details-display-cards/pill-status-trigger";
import ConfirmDialog from "components/shared/confirm-dialog";
import {
  ENABLED_BILLING_CONTACT_TYPES,
  PillStatusMode,
  billingContactTypeConfig,
  contactTypeDescriptionMapping,
  contactTypeDisplayMapping,
} from "constants/index";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import {
  duplicateEmailIndices,
  exceedsEmailsLengthLimit,
  normaliseEmail,
} from "helpers/billing-contacts";
import { toUUID } from "helpers/s12-helpers";
import { isEmail2 } from "helpers/validator";
import { UrlParamTypes } from "types/org-display";

type ContactRowState = {
  firstName: string;
  lastName: string;
  email: string;
};

const emptyRow = (): ContactRowState => ({
  firstName: "",
  lastName: "",
  email: "",
});

const toRowState = (contact: BillingContact): ContactRowState => ({
  firstName: contact.firstName,
  lastName: contact.lastName,
  email: contact.email,
});

// A single editable contact row. Name fields are rendered when the contact type
// shows them, and only flagged as errors when that type also requires them.
const ContactRow = ({
  row,
  showName,
  requireName,
  emailError,
  emailHelperText,
  testIdPrefix,
  index,
  onChange,
  onRemove,
}: {
  row: ContactRowState;
  showName: boolean;
  requireName: boolean;
  emailError: boolean;
  emailHelperText?: string;
  testIdPrefix: string;
  index: number;
  onChange: (key: keyof ContactRowState, value: string) => void;
  onRemove: () => void;
}) => {
  return (
    <Grid container spacing={1} alignItems="center">
      {showName && (
        <>
          <Grid item xs={6} sm={3}>
            <TextField
              label="First name"
              inputProps={{
                "data-testid": `${testIdPrefix}-first-name-field-${index}`,
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={row.firstName}
              onChange={(e) => onChange("firstName", e.target.value)}
              error={requireName && !row.firstName}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Last name"
              inputProps={{
                "data-testid": `${testIdPrefix}-last-name-field-${index}`,
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={row.lastName}
              onChange={(e) => onChange("lastName", e.target.value)}
              error={requireName && !row.lastName}
            />
          </Grid>
        </>
      )}
      <Grid item xs={10} sm={showName ? 5 : 11}>
        <TextField
          label="Email address"
          inputProps={{
            "data-testid": `${testIdPrefix}-email-field-${index}`,
          }}
          fullWidth
          margin="normal"
          variant="outlined"
          value={row.email}
          onChange={(e) => onChange("email", e.target.value)}
          error={emailError}
          helperText={emailHelperText}
        />
      </Grid>
      <Grid item xs={2} sm={1}>
        <Tooltip title="Remove">
          <IconButton
            aria-label="Remove contact"
            data-testid={`${testIdPrefix}-remove-${index}`}
            onClick={onRemove}
          >
            <DeleteOutline />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export interface BillingContactsFormProps {
  /** For a caller that already labels the field - the dashboard's cards. */
  iconOnly?: boolean;
}

const BillingContactsForm = ({ iconOnly }: BillingContactsFormProps) => {
  const { showError } = useContext(SnackbarContext);
  const { roleId, regionCode } = useParams<UrlParamTypes>();

  const [inProgress, setInProgress] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [statusMode, setStatusMode] = useState<PillStatusMode>(
    PillStatusMode.CREATE,
  );

  // Editable rows per enabled contact type.
  const [rowsByType, setRowsByType] = useState<
    Record<number, ContactRowState[]>
  >({});
  // Contacts of any type not in ENABLED_BILLING_CONTACT_TYPES. Preserved and
  // re-sent on update because UpdateBillingContacts replaces the full set.
  const [preservedContacts, setPreservedContacts] = useState<BillingContact[]>(
    [],
  );
  const [poRequired, setPoRequired] = useState(false);

  const buildRowsFromContacts = useCallback((contacts: BillingContact[]) => {
    const rows: Record<number, ContactRowState[]> = {};
    ENABLED_BILLING_CONTACT_TYPES.forEach((type) => {
      // No stored contacts means no rows — the user adds one explicitly via
      // "Add contact". A seeded empty row would make "no contacts" look like
      // "one blank contact" and leave no way to reach zero.
      rows[type] = contacts
        .filter((c) => c.contactType === type)
        .map(toRowState);
    });
    return rows;
  }, []);

  const fetchBillingContacts = useCallback(() => {
    getBillingContactsByOrg(
      { orgId: toUUID(roleId) },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((response) => {
        const contacts = response?.billingContacts ?? [];
        setPreservedContacts(
          contacts.filter(
            (c) => !ENABLED_BILLING_CONTACT_TYPES.includes(c.contactType),
          ),
        );
        setRowsByType(buildRowsFromContacts(contacts));
        setPoRequired(response?.poRequired ?? false);

        const hasEnabledContacts = contacts.some((c) =>
          ENABLED_BILLING_CONTACT_TYPES.includes(c.contactType),
        );
        setStatusMode(
          hasEnabledContacts ? PillStatusMode.EDIT : PillStatusMode.CREATE,
        );
      })
      .catch((err) => {
        if (err.code === gRPCStatus.NOT_FOUND) {
          setStatusMode(PillStatusMode.CREATE);
          setRowsByType(buildRowsFromContacts([]));
        } else {
          setStatusMode(PillStatusMode.CHECK);
        }
      });
  }, [regionCode, roleId, buildRowsFromContacts]);

  useEffect(() => {
    fetchBillingContacts();
  }, [fetchBillingContacts]);

  const getRows = (type: ContactType): ContactRowState[] =>
    rowsByType[type] ?? [];

  const handleRowChange = (
    type: ContactType,
    index: number,
    key: keyof ContactRowState,
    value: string,
  ) => {
    setRowsByType((prev) => {
      const rows = [...(prev[type] ?? [])];
      rows[index] = {
        ...rows[index],
        [key]: key === "email" ? normaliseEmail(value) : value,
      };
      return { ...prev, [type]: rows };
    });
  };

  const handleAddRow = (type: ContactType) => {
    setRowsByType((prev) => ({
      ...prev,
      [type]: [...(prev[type] ?? []), emptyRow()],
    }));
  };

  const handleRemoveRow = (type: ContactType, index: number) => {
    setRowsByType((prev) => ({
      ...prev,
      [type]: (prev[type] ?? []).filter((_, i) => i !== index),
    }));
  };

  // Validation, per enabled contact type.
  // Emails must be unique within a contact type's own rows (not necessarily
  // across types).
  const duplicateEmailIndicesByType: Record<number, Set<number>> = {};
  ENABLED_BILLING_CONTACT_TYPES.forEach((type) => {
    duplicateEmailIndicesByType[type] = duplicateEmailIndices(
      getRows(type).map((r) => r.email),
    );
  });
  const hasDuplicateEmails = Object.values(duplicateEmailIndicesByType).some(
    (indices) => indices.size > 0,
  );

  const invoiceDeliveryEmails = getRows(ContactType.INVOICE_DELIVERY).map(
    (r) => r.email,
  );
  const exceedsLength = exceedsEmailsLengthLimit(invoiceDeliveryEmails);

  const hasInvalidRow = ENABLED_BILLING_CONTACT_TYPES.some((type) => {
    const { requireName } = billingContactTypeConfig[type];
    return getRows(type).some((row) => {
      const isBlank = !row.email && !row.firstName && !row.lastName;
      // A blank row means "no contact of this type" for optional-name types —
      // it's skipped on submit. Required-name types must be filled in or
      // removed instead of left blank, since the user explicitly added them.
      if (isBlank) return requireName;
      if (!row.email) return true; // Partially filled without an email.
      if (!isEmail2(row.email)) return true;
      return requireName && (!row.firstName || !row.lastName);
    });
  });

  const isSubmitDisabled =
    inProgress || hasInvalidRow || hasDuplicateEmails || exceedsLength;

  const submitForm = () => {
    setInProgress(true);

    try {
      const editedContacts: BillingContact[] = [];
      ENABLED_BILLING_CONTACT_TYPES.forEach((type) => {
        getRows(type).forEach((row) => {
          if (!row.email) return; // skip blank rows
          editedContacts.push(
            new BillingContact({
              firstName: row.firstName.trim(),
              lastName: row.lastName.trim(),
              email: normaliseEmail(row.email),
              contactType: type,
            }),
          );
        });
      });

      updateBillingContacts(
        {
          orgId: toUUID(roleId),
          billingContacts: [...preservedContacts, ...editedContacts],
          poRequired,
        },
        callOptionsHeaders(regionCode, roleId),
      )
        .catch((error) => {
          showError(error);
        })
        .finally(() => {
          fetchBillingContacts();
          setInProgress(false);
        });
    } catch (error) {
      // Guard against synchronous failures (e.g. building the request) so the
      // saving indicator can never get stuck on.
      showError(error as GrpcError);
      setInProgress(false);
    }
  };

  const handleOnConfirm = () => {
    if ([PillStatusMode.CREATE, PillStatusMode.EDIT].includes(statusMode)) {
      submitForm();
    }
  };

  const shouldSpin = inProgress ? "spin-1s" : "";
  const billingContactsStatus = () => {
    if (inProgress) {
      return (
        <PillStatusTrigger
          label="Saving billing contacts…"
          testId="pill-status-saving-billing-contacts"
          disabled
          iconOnly={iconOnly}
          icon={<Autorenew className={shouldSpin} fontSize="small" />}
        />
      );
    }
    switch (statusMode) {
      case PillStatusMode.EDIT: {
        return (
          <PillStatusTrigger
            label="Edit billing contacts"
            testId="pill-status-edit-billing-contacts"
            disabled={inProgress}
            iconOnly={iconOnly}
            textColor="#178a02"
            onClick={() => setOpenConfirmDialog(true)}
            icon={<EditOutlined style={{ color: "#178a02" }} />}
          />
        );
      }
      case PillStatusMode.CREATE: {
        return (
          <PillStatusTrigger
            label="Add billing contacts"
            testId="pill-status-create-billing-contacts"
            color="warning"
            disabled={inProgress}
            iconOnly={iconOnly}
            onClick={() => setOpenConfirmDialog(true)}
            icon={
              inProgress ? (
                <Autorenew className={shouldSpin} />
              ) : (
                <AddCircleOutlineOutlined fontSize="small" />
              )
            }
          />
        );
      }
      default: {
        return (
          <PillStatusTrigger
            label="Check billing contacts"
            testId="pill-status-check-billing-contacts"
            disabled={inProgress}
            iconOnly={iconOnly}
            onClick={fetchBillingContacts}
            icon={<Autorenew className={shouldSpin} />}
          />
        );
      }
    }
  };

  const renderContactTypeSection = (type: ContactType) => {
    const { showName, requireName } = billingContactTypeConfig[type];
    const rows = getRows(type);
    const isInvoiceDelivery = type === ContactType.INVOICE_DELIVERY;
    const testIdPrefix = `billing-contact-${type}`;
    const duplicateIndices = duplicateEmailIndicesByType[type];

    return (
      <React.Fragment key={type}>
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Divider textAlign="left" sx={{ "&::before": { width: 0 } }}>
            <Typography variant="body1" fontWeight="bold">
              {contactTypeDisplayMapping[type]}
            </Typography>
          </Divider>
          <Typography variant="caption" color="text.secondary">
            {contactTypeDescriptionMapping[type]}
          </Typography>
        </Grid>
        {rows.map((row, index) => {
          const isDuplicate = duplicateIndices.has(index);
          const showEmailError =
            (!!row.email && !isEmail2(row.email)) ||
            (isDuplicate && !!row.email);
          let emailHelperText: string | undefined;
          if (!!row.email && !isEmail2(row.email)) {
            emailHelperText = "Invalid email address";
          } else if (isDuplicate && !!row.email) {
            emailHelperText = "Duplicate email address";
          }

          return (
            <Grid item xs={12} key={`${type}-${index}`}>
              <ContactRow
                row={row}
                index={index}
                showName={showName}
                requireName={requireName}
                emailError={showEmailError}
                emailHelperText={emailHelperText}
                testIdPrefix={testIdPrefix}
                onChange={(key, value) =>
                  handleRowChange(type, index, key, value)
                }
                onRemove={() => handleRemoveRow(type, index)}
              />
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <Button
            size="small"
            startIcon={<AddCircleOutlineOutlined />}
            data-testid={`${testIdPrefix}-add-row`}
            onClick={() => handleAddRow(type)}
          >
            Add contact
          </Button>
        </Grid>
        {isInvoiceDelivery && exceedsLength && (
          <Grid item xs={12}>
            <FormHelperText error data-testid="billing-contacts-length-error">
              Total email length exceeds 1200 characters, please remove some.
            </FormHelperText>
          </Grid>
        )}
      </React.Fragment>
    );
  };

  const billingContactsDialogue = () => {
    return (
      <DialogContent data-testid="billing-contacts-form">
        <Grid container spacing={1}>
          {ENABLED_BILLING_CONTACT_TYPES.map((type) =>
            renderContactTypeSection(type),
          )}
        </Grid>
      </DialogContent>
    );
  };

  return (
    <>
      {billingContactsStatus()}
      <ConfirmDialog
        title="Billing contacts"
        width="md"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={handleOnConfirm}
        onCancel={fetchBillingContacts}
        // Dismissing via the backdrop skips onCancel, so re-fetch here too and
        // discard any pending edits (including removed rows).
        onClose={fetchBillingContacts}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="OK"
        disabled={isSubmitDisabled}
      >
        {billingContactsDialogue()}
      </ConfirmDialog>
    </>
  );
};

export default BillingContactsForm;
